/**
 * NextBotTouchNote (KAN-815)
 *
 * When and what the bot writes to the client next: "Бот напишет 15.10 в 17:26
 * · шаг 1 из 3 · <summary>" (Moscow time) with "Отправить сейчас" (KAN-816:
 * no "Не писать", manual mode covers it). A manager task step reads "Задача
 * менеджеру ..." and its button "Поставить сейчас". The send-now result
 * is a toast of the hook; a refused action (409) shows the reason inline. The buttons follow the access rules of the MANAGER role.
 * KAN-817: "Показать текст" generates the reminder text now without sending
 * it ("Другой вариант" when a draft exists); the draft is shown under the line,
 * a skip / later draft as a muted line, an expired one muted with a note, and
 * always its time. Only for steps the bot writes itself. Only the error of the
 * latest action is shown (the other mutation is reset on click).
 * The compact variant (CRM row) is an icon with the time, the full line (and
 * the draft) in the title, no buttons. Nothing is rendered without a planned touch.
 * KAN-858: allowCancel adds "Не писать" (the manager app); the cabinet keeps
 * manual mode for that (KAN-816).
 */
import { useState } from 'react';
import { Bot } from 'lucide-react';
import { useNextBotTouchActions } from '@/hooks/useNextBotTouch';
import { useCanCallApi } from '@/hooks/useApiAccess';
import { extractErrorMessage } from '@/lib/extractErrorMessage';
import {
  DRAFT_EXPIRED_LINE,
  formatNextBotTouchDraft,
  formatNextBotTouchDraftTime,
  formatNextBotTouchLine,
  formatNextBotTouchShort,
  formatNextBotTouchTitle,
  pickNextBotTouchDraft,
} from '@/lib/nextBotTouch';
import type { NextBotTouch, NextBotTouchDraft } from '@/types/nextBotTouch';

interface NextBotTouchNoteProps {
  clientId: string;
  touch: NextBotTouch | null | undefined;
  variant?: 'full' | 'compact';
  /** KAN-858: show "Не писать" (POST next-bot-touch/cancel/). */
  allowCancel?: boolean;
}

const BUTTON_CLASS =
  'rounded border border-sky-300 bg-white px-2 py-0.5 text-xs font-medium text-sky-800 hover:bg-sky-100 disabled:cursor-not-allowed disabled:opacity-50';

export function NextBotTouchNote({
  clientId,
  touch,
  variant = 'full',
  allowCancel = false,
}: NextBotTouchNoteProps) {
  if (!touch) return null;
  if (variant === 'compact') {
    return (
      <span
        data-testid="next-bot-touch-note"
        title={formatNextBotTouchTitle(touch)}
        className="inline-flex items-center gap-1 whitespace-nowrap text-xs text-gray-500"
      >
        <Bot className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
        {formatNextBotTouchShort(touch)}
      </span>
    );
  }
  return <FullNote clientId={clientId} touch={touch} allowCancel={allowCancel} />;
}

interface FullNoteProps {
  clientId: string;
  touch: NextBotTouch;
  allowCancel: boolean;
}

function FullNote({ clientId, touch, allowCancel }: FullNoteProps) {
  const canSend = useCanCallApi('POST', `/api/v1/clients/${clientId}/next-bot-touch/send-now/`);
  const canDraft = useCanCallApi('POST', `/api/v1/clients/${clientId}/next-bot-touch/draft/`);
  const canCancelApi = useCanCallApi('POST', `/api/v1/clients/${clientId}/next-bot-touch/cancel/`);
  const { sendNow, draft, cancel } = useNextBotTouchActions(clientId);
  const canCancel = allowCancel && canCancelApi;
  // The prop at the moment of the draft click: a different object later means
  // the refresh after the action has arrived and the prop can be trusted.
  const [touchAtDraft, setTouchAtDraft] = useState<NextBotTouch | null>(null);
  const shownDraft = touch.sent_by_bot
    ? pickNextBotTouchDraft(touch, draft.data?.next_bot_touch, touch !== touchAtDraft)
    : null;
  const onSend = () => {
    draft.reset();
    cancel.reset();
    sendNow.mutate();
  };
  const onCancel = () => {
    draft.reset();
    sendNow.reset();
    cancel.mutate();
  };
  const onDraft = () => {
    sendNow.reset();
    cancel.reset();
    setTouchAtDraft(touch);
    draft.mutate();
  };
  const busy = sendNow.isPending || draft.isPending || cancel.isPending;
  const error = draft.error ?? sendNow.error ?? cancel.error;
  const showDraftButton = canDraft && touch.sent_by_bot;

  return (
    <div
      data-testid="next-bot-touch-note"
      className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1 rounded-md bg-sky-50 px-2 py-1 text-sm text-sky-900"
    >
      <span data-testid="next-bot-touch-text" className="inline-flex min-w-0 items-start gap-1 break-words">
        <Bot className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
        <span className="min-w-0">{formatNextBotTouchLine(touch)}</span>
      </span>
      {(canSend || showDraftButton || canCancel) && (
        <span className="flex flex-wrap items-center gap-2">
          {canSend && (
            <button
              type="button"
              data-testid="next-bot-touch-send-now"
              onClick={onSend}
              disabled={busy}
              className={BUTTON_CLASS}
            >
              {touch.sent_by_bot ? 'Отправить сейчас' : 'Поставить сейчас'}
            </button>
          )}
          {showDraftButton && (
            <button
              type="button"
              data-testid="next-bot-touch-draft-button"
              onClick={onDraft}
              disabled={busy}
              className={BUTTON_CLASS}
            >
              {draft.isPending ? 'Пишу черновик...' : shownDraft ? 'Другой вариант' : 'Показать текст'}
            </button>
          )}
          {canCancel && (
            <button
              type="button"
              data-testid="next-bot-touch-cancel"
              onClick={onCancel}
              disabled={busy}
              className={BUTTON_CLASS}
            >
              Не писать
            </button>
          )}
        </span>
      )}
      {shownDraft && <DraftBlock draft={shownDraft} />}
      {error && (
        <span data-testid="next-bot-touch-error" role="alert" className="w-full text-xs text-red-600">
          {extractErrorMessage(error, 'Не удалось выполнить действие')}
        </span>
      )}
    </div>
  );
}

function DraftBlock({ draft }: { draft: NextBotTouchDraft }) {
  const time = formatNextBotTouchDraftTime(draft);
  return (
    <div className="flex w-full min-w-0 flex-col gap-0.5">
      {draft.kind === 'text' ? (
        <blockquote
          data-testid="next-bot-touch-draft"
          className={`max-h-48 w-full min-w-0 overflow-y-auto whitespace-pre-wrap break-words border-l-2 px-2 py-1 text-sm sm:max-h-64 ${
            draft.expired ? 'border-gray-300 bg-white/40 text-gray-500' : 'border-sky-300 bg-white/70 text-gray-800'
          }`}
        >
          {draft.text}
        </blockquote>
      ) : (
        <span data-testid="next-bot-touch-draft" className="text-xs italic text-gray-500">
          {formatNextBotTouchDraft(draft)}
        </span>
      )}
      {draft.expired && (
        <span data-testid="next-bot-touch-draft-expired" className="text-xs text-gray-500">
          {DRAFT_EXPIRED_LINE}
        </span>
      )}
      {time && (
        <span data-testid="next-bot-touch-draft-time" className="text-xs text-gray-500">
          {time}
        </span>
      )}
    </div>
  );
}
